import { useEffect, useState, useCallback } from "react";
import axios from "axios";

/**
 * useGooglePay — Google Pay via Tranzila's Hosted Fields SDK (`thostedf.js`).
 *
 * ── Why a SHARED (single) Tranzila instance ────────────────────────────────
 * Tranzila's `thostedf.js` is a thin relay. The real Google Pay logic lives in
 * a HIDDEN control iframe (`controlfields.html` -> `ops_gpay.js` + Google's
 * `pay.js`) that each `TzlaHostedFields.create()` spins up independently. Every
 * `create()` therefore builds its own control iframe, its own Google
 * `PaymentsClient`, its own `isReadyToPay()` call, and its own GLOBAL parent
 * `message` listener (no instance id on the `isGpayEnable` message).
 *
 * Four `create()` calls (one per checkout surface) => four `isReadyToPay()`
 * calls near-simultaneously, which trips Chrome's per-method `canMakePayment`
 * quota (~1 distinct query / 30 min) and makes `googleIsEnable` fire for
 * none/one NON-DETERMINISTICALLY (worked once, then vanished on reload). Google's
 * guidance: build ONE PaymentsClient and reuse it. We build ONE TzlaHostedFields
 * instance for the whole page.
 *
 * ── Why the previous singleton (ed86416e) killed the button ────────────────
 * It delivered the async `googleIsEnable` ONLY through a subscriber Set that each
 * component PRUNED on unmount. These modals re-mount heavily during checkout, so
 * the live button's subscriber was gone by the time the event arrived. Fixed here
 * by a STICKY module flag re-read synchronously on every mount, so a late-mounting
 * button can never miss the event.
 */

const THF_SDK_URL = "https://hf.tranzila.com/assets/js/thostedf.js";
const HOST_ID = "gpay-hidden-fields"; // single shared host for the whole page
const BASE_URL = process.env.REACT_APP_BASE_URL;

// ── Rollout gate ────────────────────────────────────────────────────────────
// Google Pay is still in TEST PHASE (Tranzila's SDK can't open the sheet on
// Android at all — see the !isAndroid gates in the mobile checkout surfaces).
// It is OFF unless a deploy explicitly opts in with REACT_APP_ENABLE_GPAY=true,
// which is set only in test.blends.co.il's .env. Production omits the var, so an
// unset value MUST read as disabled — never invert this default. When disabled
// nothing initializes: no thostedf.js, no control iframe, no readiness probe, and
// every surface falls back to its normal payment button.
// NOTE: CRA inlines REACT_APP_* at BUILD time, so flipping this needs a rebuild.
const GPAY_ENABLED = String(process.env.REACT_APP_ENABLE_GPAY).toLowerCase() === "true";

// Server-side debug (same endpoint Apple Pay debugging uses) — mobile has no
// console, so every Google Pay lifecycle event is mirrored into the SERVER log.
const gpayDebug = (step, data) => {
  try {
    axios.post(`${BASE_URL}/apple-pay-debug`, {
      step: `GPAY_${step}`,
      data: { ...data, ua: navigator.userAgent.slice(0, 80) },
      timestamp: new Date().toISOString(),
    }).catch(() => {});
  } catch (_) { /* never break checkout for logging */ }
};

// ── Eligibility FALLBACK ────────────────────────────────────────────────────
// Tranzila's `googleIsEnable` rides a quota-sensitive readiness check and goes
// silent under repeated loads even when the browser CAN pay (observed all day:
// every raw isReadyToPay basic probe returned true while the event stayed mute —
// and the charge path itself, chargeGpay -> loadPaymentData, does NOT depend on
// the event; it opened the card picker whenever the button was clickable).
// So: if the event hasn't fired within FALLBACK_MS of create(), ask Google's own
// pay.js ONCE (basic isReadyToPay — the quota-safe variant, no
// existingPaymentMethodRequired) and trust its answer for DISPLAY. This runs only
// when Tranzila's event is missing, so the steady-state cost stays one check/load.
const FALLBACK_MS = 6000;
let fallbackTimer = null;

function scheduleEnableFallback() {
  if (fallbackTimer) return;
  fallbackTimer = setTimeout(() => {
    if (sharedEligible) return; // Tranzila's event arrived — nothing to do
    const start = () => {
      try {
        const client = new window.google.payments.api.PaymentsClient({ environment: "PRODUCTION" });
        client.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [{
            type: "CARD",
            parameters: {
              allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
              allowedCardNetworks: ["VISA", "MASTERCARD", "AMEX"],
            },
          }],
        }).then((r) => {
          if (r && r.result && !sharedEligible) {
            sharedEligible = true;
            console.log("[useGooglePay] eligibility via pay.js fallback (Tranzila event silent)");
            gpayDebug("ENABLE_VIA_FALLBACK", {});
            notify();
          } else if (!r || !r.result) {
            gpayDebug("FALLBACK_NOT_READY", r || {});
          }
        }).catch((e) => gpayDebug("FALLBACK_ERR", { message: e && e.message }));
      } catch (e) {
        gpayDebug("FALLBACK_ERR", { message: e && e.message });
      }
    };
    if (window.google && window.google.payments && window.google.payments.api) {
      start();
    } else {
      const s = document.createElement("script");
      s.src = "https://pay.google.com/gp/p/js/pay.js";
      s.async = true;
      s.onload = start;
      s.onerror = () => gpayDebug("FALLBACK_PAYJS_BLOCKED", {});
      document.body.appendChild(s);
    }
  }, FALLBACK_MS);
}

// ── Module-level shared state (one per page) ────────────────────────────────
let sharedFields = null;        // the single TzlaHostedFields instance
let sharedEligible = false;     // STICKY: once true, stays true for the page
let initState = "idle";         // idle | loading | creating | ready | failed
let subSeq = 0;
const subscribers = new Map();  // id -> setter(bool); keyed so re-mounts replace

function notify() {
  // Snapshot values first so an unsubscribe during iteration can't skip anyone.
  const setters = Array.from(subscribers.values());
  for (let i = 0; i < setters.length; i++) {
    try { setters[i](true); } catch (_) { /* setter on unmounted tree — ignore */ }
  }
}

// Idempotent: safe to call from every mount; only the first call does work.
function ensureSharedInit() {
  if (!GPAY_ENABLED) return;
  if (initState === "ready" || initState === "creating" || initState === "loading") return;
  // `idle` or `failed` — (re)attempt. Allowing retry from `failed` avoids the
  // "one mount used up init forever" trap of the old singleton.

  const mountHost = () => {
    if (document.getElementById(HOST_ID)) return;
    const host = document.createElement("div");
    host.id = HOST_ID;
    host.setAttribute("aria-hidden", "true");
    host.style.cssText =
      "position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;";
    host.innerHTML =
      `<div id="${HOST_ID}-num"></div><div id="${HOST_ID}-cvv"></div><div id="${HOST_ID}-exp"></div>`;
    document.body.appendChild(host);
  };

  const createInstance = () => {
    if (!window.TzlaHostedFields) {
      console.error("[useGooglePay] thostedf.js loaded but TzlaHostedFields missing");
      initState = "failed";
      return;
    }
    if (sharedFields) { initState = "ready"; return; } // guard double-create
    initState = "creating";
    try {
      mountHost();
      const fields = window.TzlaHostedFields.create({
        sandbox: false, // Tranzila: "Always use sandbox: false"
        fields: {
          credit_card_number: { selector: `#${HOST_ID}-num` },
          cvv: { selector: `#${HOST_ID}-cvv` },
          expiry: { selector: `#${HOST_ID}-exp` },
        },
      });
      sharedFields = fields;
      initState = "ready";
      console.log("[useGooglePay] TzlaHostedFields.create() OK (single shared instance) — waiting for googleIsEnable");
      gpayDebug("CREATE_OK", {});
      // If Tranzila's enable event stays silent, fall back to Google's own answer.
      scheduleEnableFallback();

      // Fires only when the client actually has Google Pay available. With ONE
      // instance there is exactly one control iframe and one parent listener, so
      // this is not racing three other iframes / tripping the canMakePayment quota.
      fields.onEvent("googleIsEnable", () => {
        if (sharedEligible) return; // dedupe
        sharedEligible = true;
        console.log("[useGooglePay] googleIsEnable fired -> Google Pay button eligible");
        gpayDebug("ENABLE_FIRED", {});
        notify();
      });
    } catch (e) {
      console.error("[useGooglePay] TzlaHostedFields.create() failed:", e);
      gpayDebug("CREATE_FAILED", { message: e && e.message });
      initState = "failed";
    }
  };

  if (window.TzlaHostedFields) {
    createInstance();
    return;
  }

  initState = "loading";
  const existing = document.querySelector(`script[src="${THF_SDK_URL}"]`);
  if (existing) {
    if (window.TzlaHostedFields) {
      createInstance();
    } else {
      existing.addEventListener("load", createInstance, { once: true });
      existing.addEventListener("error", () => {
        console.error("[useGooglePay] existing thostedf.js failed to load");
        initState = "failed";
      }, { once: true });
    }
    return;
  }

  const s = document.createElement("script");
  s.src = THF_SDK_URL;
  s.async = true;
  s.onload = createInstance;
  s.onerror = () => {
    console.error("[useGooglePay] failed to load thostedf.js (network/ad-block?) — Google Pay cannot initialize");
    gpayDebug("SDK_LOAD_FAILED", {});
    initState = "failed";
  };
  document.body.appendChild(s);
}

export default function useGooglePay() {
  // Seed from the sticky flag so a component that mounts AFTER googleIsEnable
  // already fired renders the button immediately (no missed event).
  const [googlePayEligible, setGooglePayEligible] = useState(GPAY_ENABLED && sharedEligible);

  useEffect(() => {
    if (!GPAY_ENABLED) return undefined;
    ensureSharedInit();

    // Subscribe with a stable id so a re-mount REPLACES its own slot instead of
    // orphaning it. Re-read the sticky flag in case it flipped between the
    // useState seed and the effect running.
    const id = ++subSeq;
    subscribers.set(id, setGooglePayEligible);
    if (sharedEligible) setGooglePayEligible(true);

    return () => {
      subscribers.delete(id);
      // Intentionally do NOT remove the hidden host or the shared instance here —
      // tearing them down would kill the Google Pay control iframe for every OTHER
      // mounted surface and re-trigger the readiness race. It lives for the page.
    };
  }, []);

  // Thin wrapper over the SHARED fields.chargeGpay — callback is (err, response),
  // mirroring hfChargeApple. MUST be invoked from the user gesture (button click).
  // Routes to the one live instance, so a tap can never hit a dead per-component one.
  const chargeGooglePay = useCallback((params, cb) => {
    if (!GPAY_ENABLED) {
      cb && cb({ message: "Google Pay is disabled on this environment" });
      return;
    }
    if (!sharedFields || typeof sharedFields.chargeGpay !== "function") {
      console.error("[useGooglePay] chargeGpay called before shared instance ready");
      cb && cb({ message: "Google Pay not ready" });
      return;
    }
    try {
      sharedFields.chargeGpay(params, cb);
    } catch (e) {
      cb && cb(e);
    }
  }, []);

  return { googlePayEligible, chargeGooglePay };
}
